import { useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { type ReactNode, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorState, Skeleton } from '../../components/ui/states';
import { gitApi } from '../../features/git/api';
import { errorMessage } from '../../lib/error-message';
import { ApiRequestError } from '../../lib/http';

/**
 * Đường dẫn sâu tới một việc **theo mã** — `/t/GOLD-123` (task), `/t/GOLD-I12`
 * (issue). APM-GIT-k, docs/plan-git-integration.md §8.4.
 *
 * Đích của bình luận link ngược trên MR và của *Custom issue tracker* (GitLab) /
 * *Autolink references* (GitHub): hai tính năng ấy chỉ biết mã viết trong lời
 * commit, không biết uuid nào của ChillPM. Trang hỏi API mã ấy là việc nào rồi
 * **thay** URL bằng chỗ mở việc (Board của dự án + ngăn chi tiết task, hoặc tab
 * Issue) — nút Back không quay về trang trung gian này.
 *
 * Nằm trong `_app`: chưa đăng nhập thì vỏ ứng dụng đưa về `/login?next=/t/…` và
 * quay lại đúng đây sau khi đăng nhập.
 */
export const Route = createFileRoute('/_app/t/$code')({ component: WorkItemLink });

function WorkItemLink(): ReactNode {
  const { t } = useTranslation();
  const { code } = Route.useParams();
  const navigate = useNavigate();

  const ref = useQuery({
    queryKey: ['git', 'work-item', code],
    queryFn: () => gitApi.workItem(code),
    retry: false,
  });

  useEffect(() => {
    const item = ref.data;
    if (!item) return;
    void navigate(
      item.kind === 'task'
        ? {
            to: '/projects',
            search: { projectId: item.projectId, tab: 'board', taskId: item.id },
            replace: true,
          }
        : {
            to: '/projects',
            search: { projectId: item.projectId, tab: 'issues', issueId: item.id },
            replace: true,
          },
    );
  }, [ref.data, navigate]);

  if (ref.isError) {
    const missing =
      ref.error instanceof ApiRequestError &&
      (ref.error.status === 404 || ref.error.status === 403);
    return (
      <ErrorState
        message={missing ? t('gitTab.workItem.notFound', { code }) : errorMessage(ref.error, t)}
        {...(missing ? {} : { onRetry: () => void ref.refetch() })}
      />
    );
  }
  return (
    <div className="flex flex-col gap-2" aria-busy="true">
      <p className="text-sm text-muted-foreground">{t('gitTab.workItem.opening', { code })}</p>
      <Skeleton className="h-24 w-full" />
    </div>
  );
}
